import { BriefTemplatesService } from '@inference.engine/assets-api-client/web';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { enqueueSnackbar } from 'notistack';
import { assetsHttp } from '@/lib/api/http-client';
import { SUPPRESS_ERROR_NOTIFICATION_HEADER } from '@/lib/api/interceptors/problem-details.interceptor';
import { i18n } from '@/shared/i18n';
import { BRIEF_TEMPLATES_QUERY_KEYS } from './query-keys';

type LevelAction = 'promote' | 'turnOff' | 'turnOn';

/**
 * True when the platform turned a system default off for every organization. The server refuses
 * before changing anything, so there is nothing to refresh.
 */
function isDisabledGloballyError(error: unknown): boolean {
  if (!isAxiosError(error) || error.response?.status !== 409) return false;
  const data: unknown = error.response.data;
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as Record<string, unknown>).code === 'brief_template_disabled_globally'
  );
}

function successMessage(action: LevelAction): string {
  switch (action) {
    case 'promote':
      return i18n.t('knowledge:knowledgeHub.briefTemplateLevelMutations.templatePromoted');
    case 'turnOff':
      return i18n.t('knowledge:knowledgeHub.briefTemplateLevelMutations.templateTurnedOff');
    case 'turnOn':
      return i18n.t('knowledge:knowledgeHub.briefTemplateLevelMutations.templateTurnedOn');
  }
}

function failureMessage(action: LevelAction, error: unknown): string {
  switch (action) {
    case 'promote':
      return i18n.t('knowledge:knowledgeHub.briefTemplateLevelMutations.failedToPromoteTemplate');
    case 'turnOff':
      return i18n.t('knowledge:knowledgeHub.briefTemplateLevelMutations.failedToTurnOffTemplate');
    case 'turnOn':
      return isDisabledGloballyError(error)
        ? i18n.t('knowledge:knowledgeHub.briefTemplateLevelMutations.turnedOffByPlatform')
        : i18n.t('knowledge:knowledgeHub.briefTemplateLevelMutations.failedToTurnOnTemplate');
  }
}

function useBriefTemplateLevelAction(id: string, action: LevelAction) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await BriefTemplatesService[action]({
        client: assetsHttp,
        path: { id },
        throwOnError: true,
        // The problem-details toast would print the server's English `detail`; these hooks show
        // a translated message instead, including the one specific to the 409.
        // eslint-disable-next-line i18next/no-literal-string -- HTTP header value
        headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' },
      });
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: BRIEF_TEMPLATES_QUERY_KEYS.detail(id) });
      void queryClient.invalidateQueries({ queryKey: BRIEF_TEMPLATES_QUERY_KEYS.list() });
      enqueueSnackbar(successMessage(action), { variant: 'success' });
    },
    onError: (error) => {
      enqueueSnackbar(failureMessage(action, error), { variant: 'error' });
    },
  });
}

/** Moves a workspace template to its organization, making it available in every workspace. */
export function usePromoteBriefTemplate(id: string) {
  return useBriefTemplateLevelAction(id, 'promote');
}

/** Stops offering a template in brief creation; for a system default, in the caller's organization. */
export function useTurnOffBriefTemplate(id: string) {
  return useBriefTemplateLevelAction(id, 'turnOff');
}

export function useTurnOnBriefTemplate(id: string) {
  return useBriefTemplateLevelAction(id, 'turnOn');
}
